import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useUser } from 'app/network/user';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';

import { CalendarCell } from './calendar-cell';
import type { HistoryMonth } from './types';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronLeft, IconChevronRight, IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface MonthlyStreakCardProps {
  history: HistoryMonth[];
  setOpenSeriesInfo: (show: boolean) => void;
}

export const MonthlyStreakCard = function MonthlyStreakCard({ history, setOpenSeriesInfo }: MonthlyStreakCardProps) {
  const { t } = useTranslation('streak');
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const { data: user } = useUser();

  const monthsForYear = (() => {
    return Array.from({ length: 12 }, (_, idx) => {
      const period = `${String(year)}-${String(idx + 1).padStart(2, '0')}`;
      const found = history.find((h) => h.period === period);
      return (
        found ?? {
          period,
          numUsersTarget: 0,
          targetMultiplier: 0.7,
          numLoans: 0,
          isFreeze: false,
        }
      );
    });
  })();

  const onPrev = () => setYear((y) => Math.max(2025, y - 1));
  const onNext = () => setYear((y) => Math.min(currentYear, y + 1));
  const isAtCurrentYear = year >= currentYear;

  return (
    <Column backgroundColor="white" borderRadius="radius.lg" gap="spacing.general.x4" padding="spacing.general.x4">
      <Row alignItems="center" justifyContent="space-between">
        <Row alignItems="center" gap="spacing.general.x1">
          <Text fontSize={16} fontWeight="600">
            {t('monthlyStreak')}
          </Text>
          <Pressable hitSlop={10} onPress={() => setOpenSeriesInfo(true)}>
            <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.tertiary']} />
          </Pressable>
        </Row>
        <Row alignItems="center" gap="spacing.general.x1">
          <Text fontSize={14}>🔥</Text>
          <Text fontSize={14} fontWeight="600">
            {user?.numStreak ?? 0}
          </Text>
        </Row>
      </Row>

      <Row alignItems="center" justifyContent="space-between">
        <Pressable hitSlop={10} onPress={onPrev} style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}>
          <IconChevronLeft strokeWidth={ICON_STROKE_WIDTH} />
        </Pressable>
        <Text fontSize={15} fontWeight="600">
          {String(year)}
        </Text>
        <Pressable
          disabled={isAtCurrentYear}
          hitSlop={10}
          onPress={onNext}
          style={({ pressed }) => ({ opacity: isAtCurrentYear ? 0.35 : pressed ? 0.6 : 1 })}
        >
          <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
        </Pressable>
      </Row>

      <Row flexWrap="wrap" gap="spacing.general.x1" alignItems="center" justifyContent="center">
        {monthsForYear.map((m) => (
          <CalendarCell key={m.period} month={m} />
        ))}
      </Row>
    </Column>
  );
};
